import {
  collection,
  deleteDoc,
  deleteField,
  doc,
  getDoc,
  limit,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
  where,
  type Unsubscribe,
} from '@firebase/firestore';
import { httpsCallable } from '@firebase/functions';
import { db, functions } from './firebase';
import { onSnapshotWithRetry } from './snapshotRetry';
import { COLLECTIONS } from './collections';
import { mapDoc, mapDocs } from '@utils/firestore';
import { MAX_EVENT_RSVPS, PlanEventRsvpSchema, PlanEventSchema } from '../schemas/firestoreSchemas';
import type { PlanEvent, PlanEventMeetingPlace, PlanEventRsvp } from '../types';
import { createPrefixedLogger } from '../utils/logger';

const log = createPrefixedLogger('api:planEvents');

// Events are top-level (planEvents/{eventId}) and link to a plan's bundle.
// They are world-readable: an event is an invitation. Sign-ups live under the
// event, owner-read only, and only the rsvpToPlanEvent Cloud Function writes
// them (it also keeps goingCount). This client never writes either.

function eventsCollection() {
  return collection(db, COLLECTIONS.PLAN_EVENTS);
}
function eventDoc(eventId: string) {
  return doc(db, COLLECTIONS.PLAN_EVENTS, eventId);
}
function rsvpsCollection(eventId: string) {
  return collection(db, COLLECTIONS.PLAN_EVENTS, eventId, COLLECTIONS.PLAN_EVENT_RSVPS);
}

/** What the owner's form produces. Optional text is '' or absent when blank. */
export interface PlanEventInput {
  title: string;
  startsAt: Date;
  endsAt: Date;
  timeZone: string;
  meetingPlace: PlanEventMeetingPlace;
  whatToBring?: string;
  notes?: string;
}

// Firestore rejects undefined, and the schema's lat/lng are optional.
function cleanPlace(place: PlanEventMeetingPlace): PlanEventMeetingPlace {
  const out: PlanEventMeetingPlace = { label: place.label };
  if (typeof place.lat === 'number' && typeof place.lng === 'number') {
    out.lat = place.lat;
    out.lng = place.lng;
  }
  return out;
}

export const planEventsApi = {
  newEventId(): string {
    return doc(eventsCollection()).id;
  },

  /**
   * Creates a scheduled event linked to one bundle (PLAN_EVENT_LINKS_V1 is 1
   * for now, so exactly one link). The field set matches the
   * rules' create check exactly: one link, planIds and bundleKeys mirroring
   * it, status 'scheduled', goingCount 0, and server timestamps.
   */
  async createEvent(
    eventId: string,
    ownerUid: string,
    link: { planId: string; bundleId: string },
    input: PlanEventInput
  ): Promise<void> {
    const data: Record<string, unknown> = {
      ownerUid,
      links: [{ planId: link.planId, bundleId: link.bundleId }],
      planIds: [link.planId],
      bundleKeys: [`${link.planId}/${link.bundleId}`],
      title: input.title,
      startsAt: Timestamp.fromDate(input.startsAt),
      endsAt: Timestamp.fromDate(input.endsAt),
      timeZone: input.timeZone,
      meetingPlace: cleanPlace(input.meetingPlace),
      status: 'scheduled',
      goingCount: 0,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
    if (input.whatToBring?.trim()) data.whatToBring = input.whatToBring.trim();
    if (input.notes?.trim()) data.notes = input.notes.trim();
    await setDoc(eventDoc(eventId), data);
  },

  /** Changes the fields the form edits. A blank optional text field is removed. */
  async updateEvent(eventId: string, input: PlanEventInput): Promise<void> {
    await updateDoc(eventDoc(eventId), {
      title: input.title,
      startsAt: Timestamp.fromDate(input.startsAt),
      endsAt: Timestamp.fromDate(input.endsAt),
      timeZone: input.timeZone,
      meetingPlace: cleanPlace(input.meetingPlace),
      whatToBring: input.whatToBring?.trim() ? input.whatToBring.trim() : deleteField(),
      notes: input.notes?.trim() ? input.notes.trim() : deleteField(),
      updatedAt: serverTimestamp(),
    });
  },

  async cancelEvent(eventId: string): Promise<void> {
    await updateDoc(eventDoc(eventId), { status: 'cancelled', updatedAt: serverTimestamp() });
  },

  async deleteEvent(eventId: string): Promise<void> {
    await deleteDoc(eventDoc(eventId));
  },

  async getEvent(eventId: string): Promise<PlanEvent | null> {
    return mapDoc(await getDoc(eventDoc(eventId)), PlanEventSchema);
  },

  /** One event, live: the public page uses it so a cancel or a new sign-up shows without a reload. */
  subscribeEvent(
    eventId: string,
    onNext: (event: PlanEvent | null) => void,
    onError?: (error: Error) => void
  ): Unsubscribe {
    return onSnapshotWithRetry(
      eventDoc(eventId),
      (snap) => onNext(mapDoc(snap, PlanEventSchema)),
      (error) => {
        log.warn('subscribeEvent failed:', error);
        onError?.(error);
      }
    );
  },

  /**
   * Every event linked to a plan. An array-contains filter with no orderBy
   * runs on the automatic index; a plan has a handful of events, so the store
   * sorts here rather than asking for a composite index.
   */
  subscribePlanEvents(
    planId: string,
    onNext: (events: PlanEvent[]) => void,
    onError?: (error: Error) => void
  ): Unsubscribe {
    const q = query(eventsCollection(), where('planIds', 'array-contains', planId));
    return onSnapshotWithRetry(
      q,
      (snap) => onNext(mapDocs(snap, PlanEventSchema)),
      (error) => {
        log.warn('subscribePlanEvents failed:', error);
        onError?.(error);
      }
    );
  },

  /** Sign-ups for one event. Owner only (rules); anyone else is denied. */
  subscribeRsvps(
    eventId: string,
    onNext: (rsvps: PlanEventRsvp[]) => void,
    onError?: (error: Error) => void
  ): Unsubscribe {
    const q = query(rsvpsCollection(eventId), orderBy('createdAt', 'asc'), limit(MAX_EVENT_RSVPS));
    return onSnapshotWithRetry(
      q,
      (snap) => onNext(mapDocs(snap, PlanEventRsvpSchema)),
      (error) => {
        log.warn('subscribeRsvps failed:', error);
        onError?.(error);
      }
    );
  },
};

// ----- sign-up (Cloud Function) -------------------------------------------------

export interface RsvpRequest {
  eventId: string;
  name: string;
  email: string;
  partySize: number;
  /** Honeypot. Always '' from a real person; a bot that fills every field trips it. */
  website: string;
}

export interface RsvpResult {
  ok: true;
  goingCount: number;
  /** True when this email had already signed up and the sign-up was changed. */
  updated: boolean;
}

export type RsvpErrorKind =
  | 'invalid'
  | 'not-found'
  | 'closed'
  | 'full'
  | 'rate-limited'
  | 'unknown';

export interface RsvpError {
  kind: RsvpErrorKind;
  /** Plain copy, ready to show. */
  message: string;
}

const RSVP_ERROR_COPY: Record<RsvpErrorKind, string> = {
  invalid: 'Check your name and email, then try again.',
  'not-found': "We couldn't find this event.",
  closed: 'Sign-ups for this event are closed.',
  full: 'This event is full.',
  'rate-limited': 'Too many tries. Wait a little, then try again.',
  unknown: "That didn't go through. Try again in a moment.",
};

/**
 * Maps the callable's HttpsError onto one of our kinds. The server uses
 * failed-precondition for both 'closed' and 'full' and says which in the
 * message; anything else on that code is treated as closed.
 */
export function toRsvpError(error: unknown): RsvpError {
  const code = (error as { code?: string } | null)?.code ?? '';
  const message = (error as { message?: string } | null)?.message ?? '';
  const bare = code.replace(/^functions\//, '');
  let kind: RsvpErrorKind;
  switch (bare) {
    case 'invalid-argument':
      kind = 'invalid';
      break;
    case 'not-found':
      kind = 'not-found';
      break;
    case 'failed-precondition':
      kind = /full/i.test(message) ? 'full' : 'closed';
      break;
    case 'resource-exhausted':
      kind = 'rate-limited';
      break;
    default:
      kind = 'unknown';
  }
  // The function's invalid-argument messages are written for people ("Enter a
  // valid email address."), so they beat the generic line. The rest we word.
  return { kind, message: kind === 'invalid' && message ? message : RSVP_ERROR_COPY[kind] };
}

/** Signs someone up with a name and email. No account; the function checks everything again. */
export async function rsvpToPlanEvent(request: RsvpRequest): Promise<RsvpResult> {
  try {
    const callable = httpsCallable<RsvpRequest, RsvpResult>(functions, 'rsvpToPlanEvent');
    const result = await callable(request);
    return result.data;
  } catch (error) {
    const mapped = toRsvpError(error);
    // Bad input and a closed event are expected; only the rest are worth a log.
    if (mapped.kind === 'unknown') log.error('rsvpToPlanEvent failed:', error);
    throw mapped;
  }
}
